// keep-popover-open-on-multiselect.directive.ts
import { Directive, OnDestroy, Host, inject, Signal } from '@angular/core';
import { MultiSelect } from 'primeng/multiselect';

@Directive({
  selector: 'p-multiselect[stopOnOptionSelectPropagation]',
  standalone: true
})
export class StopOnOptionSelectPropagationMultiSelectDirective implements OnDestroy {
  private restore?: () => void;

  constructor(@Host() multiSelect: MultiSelect) {
    const orig = multiSelect.onOptionSelect.bind(multiSelect);

    (multiSelect as any).onOptionSelect = (event: any, option: any) => {
      // Stop the "option click" from reaching document listeners (e.g., p-popover outside click)
      try { event?.stopImmediatePropagation?.(); } catch {}
      try { event?.stopPropagation?.(); } catch {}
      return orig(event, option);
    };

    this.restore = () => ((multiSelect as any).onOptionSelect = orig);
  }

  ngOnDestroy() {
    try { this.restore?.(); } catch {}
  }
}

/**
 * Directive to monkey-patch MultiSelect to preserve selected items
 * even when they're no longer in the options array.
 * 
 * @usage
 * <p-multiselect preserveSelection [options]="options" [(ngModel)]="selectedValues">
 *   <ng-template let-items pTemplate="selecteditems">
 *     <div *ngFor="let item of items">{{ item }}</div>
 *   </ng-template>
 * </p-multiselect>
 */
@Directive({
  selector: '[preserveSelection]',
  standalone: true
})
export class MultiSelectPreserveSelectionDirective implements OnDestroy {
  multiselect: any;
  originalGetAllVisibleAndNonVisibleOptions: any;
  selectedOptionsCache: Map<any, any> = new Map();

  constructor(@Host() multiselect: MultiSelect) {
    this.multiselect = multiselect;
    this.patchGetAllVisibleAndNonVisibleOptions();
  }

  private patchGetAllVisibleAndNonVisibleOptions() {
    // Store original method
    this.originalGetAllVisibleAndNonVisibleOptions = this.multiselect['getAllVisibleAndNonVisibleOptions'].bind(this.multiselect);

    // Override the method to include selected items from modelValue
    // even if they're not in the current options array
    this.multiselect['getAllVisibleAndNonVisibleOptions'] = () => {
      const visibleOptions = this.originalGetAllVisibleAndNonVisibleOptions();
      const modelValue = this.multiselect['modelValue']();
      const optionValue = this.multiselect['optionValue'];
      const optionLabel = this.multiselect['optionLabel'];

      // Update cache from the component's options array (captures items from loadSpecificItems)
      const componentOptions = this.multiselect['options'] || [];
      componentOptions.forEach((opt: any) => {
        const key = opt[optionValue] || opt;
        // Only cache if it has a proper label (not a stub with value === label)
        if (opt[optionLabel] && opt[optionLabel] !== key) {
          this.selectedOptionsCache.set(key, opt);
        }
      });

      // Also update cache from multiselect.selectedOptions
      if (this.multiselect.selectedOptions) {
        this.multiselect.selectedOptions.forEach((opt: any) => {
          const key = opt[optionValue] || opt;
          this.selectedOptionsCache.set(key, opt);
        });
      }

      // If optionValue and optionLabel are set, the effect filters against them
      // We need to include selected items that match modelValue
      if (optionValue && optionLabel && modelValue) {
        // Get values from modelValue to match against
        const modelValues = Array.isArray(modelValue) ? modelValue : [modelValue];

        // Create a map of existing options for quick lookup
        const existingOptionsMap = new Map();
        visibleOptions.forEach((opt: any) => {
          const key = opt[optionValue] || opt;
          existingOptionsMap.set(key, opt);
        });

        // Build result array: visible options + missing selected items
        const result = [...visibleOptions];

        // Add selected items from modelValue that aren't in visible options
        modelValues.forEach((val: any) => {
          const key = val;
          if (!existingOptionsMap.has(key)) {
            // Try to get the full object from the cache first
            const cachedOption = this.selectedOptionsCache.get(key);
            if (cachedOption) {
              // Use the cached full object with all fields
              result.push(cachedOption);
            } else {
              // Fallback to creating a stub object for the missing selected item
              const stubOption: any = {};
              stubOption[optionValue] = val;
              stubOption[optionLabel] = val; // Use value as label fallback
              result.push(stubOption);
            }
          }
        });
        
        return result;
      }

      // Default behavior when optionValue/optionLabel not set
      return visibleOptions;
    };
  }

  ngOnDestroy() {
    // Restore original method when directive is destroyed
    if (this.originalGetAllVisibleAndNonVisibleOptions) {
      this.multiselect['getAllVisibleAndNonVisibleOptions'] = this.originalGetAllVisibleAndNonVisibleOptions;
    }
  }
}
